/**
 * Defensive card definitions + effects (HANDOFF §3.7) — Agent C (cards/AI).
 *
 * PURE data + pure effect functions. NO DOM. Randomness only via injected rng
 * (hazard placement, wind direction, which tee gets roughed).
 *
 * PHASE 1 CONTRACT — Agent A calls `applyDefense` from the reducer when a defense
 * card is played, and may call `placeHazard` directly. Agent C implements both
 * using DEFENSE / HAZARD_RADIUS / DEFENSE_CARD_DEFS from ./constants.ts.
 */
import type {
  DefenseCard,
  GameState,
  Green,
  Hazard,
  HazardType,
  Rng,
  ShapeRegion,
  Side,
  SideState,
  Vec2,
  Wind,
} from './types.ts';
import { DEFENSE, DEFENSE_CARD_DEFS, FIELD } from './constants.ts';
import { greenBoundaryPoint, makeGreenShape, pointInScaledGreen } from './greens.ts';

const TWO_PI = Math.PI * 2;

/** The deck of defensive cards (§3.7), sourced from the constants table. */
export const DEFENSE_CARDS: readonly DefenseCard[] = DEFENSE_CARD_DEFS.map((d) => ({
  id: d.id,
  name: d.name,
  cost: d.cost,
  rarity: d.rarity,
  ...(d.hazard !== undefined ? { hazard: d.hazard } : {}),
  ...(d.radius !== undefined ? { radius: d.radius } : {}),
  ...(d.special !== undefined ? { special: d.special } : {}),
}));

/** Look up a defensive card by id. Returns undefined if not found. */
export function getDefenseCard(id: string): DefenseCard | undefined {
  return DEFENSE_CARDS.find((c) => c.id === id);
}

function vec(x: number, y: number): Vec2 {
  return { x, y };
}

function dist(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Does a hazard disc of `radius` centred at `pos` overlap a manicured surface
 * (green / fairway)? Approximated by inflating the region's outline by `radius`
 * about its centre and testing centre-inclusion — sound for the near-convex
 * ellipse+lobe shapes, and conservative (slightly over-rejecting) at the edges.
 */
function discOnSurface(pos: Vec2, radius: number, region: ShapeRegion): boolean {
  return pointInScaledGreen(pos, region, (region.radius + radius) / region.radius);
}

/**
 * Choose a hazard position in the ROUGH ringing a feature and return the placed
 * hazard (§3.7). Hazards NEVER sit on a manicured surface, never overlap an existing
 * hazard, and never spill off the field: the disc is stepped just OUTSIDE the
 * `target`'s outline along a random bearing (plus a random rough band), then rejected
 * if its disc would leave the field or overlap anything in `avoid` (the greens,
 * fairways, AND already-placed hazards passed as plain circles) — so sand AND water
 * always land fully ON-screen in the surrounding rough without stacking. Retries up
 * to `DEFENSE.hazardPlacementTries`; on exhaustion it sweeps evenly-spaced bearings
 * from the FIELD-CENTRE side for any clear, in-bounds spot.
 *
 * All randomness flows through the injected `rng` (HANDOFF §4.3) — deterministic
 * per seed.
 */
export function placeHazard(
  target: ShapeRegion,
  type: HazardType,
  radius: number,
  rng: Rng,
  avoid: readonly ShapeRegion[] = [],
  /** A preferred bearing to ring the hazard along (radians). When given, the first half of the
   *  tries cluster around it (± a small jitter) before falling back to the full random ring — used
   *  to seat a TREE in FRONT of the green (toward the incoming shots) so it sits on the line. */
  preferBearing?: number,
): Hazard {
  // Each placed hazard gets the SAME procedural ellipse+lobe shape as greens/
  // fairways (drawn from rng at the moment it's returned, so position draws stay
  // first and determinism per seed holds). The shape fits inside `radius`.
  const made = (pos: Vec2): Hazard => ({
    type,
    pos,
    radius,
    remaining: DEFENSE.hazardSeconds,
    ...makeGreenShape(radius, rng),
  });
  // Step from the target outline (along `angle`) into the rough by gap + band.
  const ringPos = (angle: number, extra: number): Vec2 => {
    const edge = greenBoundaryPoint(target, angle, 1);
    const d = dist(edge, target.center) + radius + DEFENSE.hazardRoughGap + extra;
    return vec(target.center.x + Math.cos(angle) * d, target.center.y + Math.sin(angle) * d);
  };
  // The whole disc must stay inside the field (not just the centre), else the
  // renderer clips it to a sliver at the edge and the hazard looks "missing".
  const inField = (p: Vec2): boolean =>
    p.x >= radius && p.x <= FIELD.width - radius && p.y >= radius && p.y <= FIELD.height - radius;
  // A spot is valid if its disc is fully on-field and clears every avoid region
  // (manicured surfaces by shape; existing hazards by container circle — no stacking).
  const valid = (p: Vec2): boolean =>
    inField(p) && !avoid.some((r) => discOnSurface(p, radius, r));

  const halfTries = DEFENSE.hazardPlacementTries / 2;
  for (let tries = 0; tries < DEFENSE.hazardPlacementTries; tries++) {
    // Bias the first half of the tries toward `preferBearing` (a tree's FRONT), then fall back
    // to the full ring. Draw order is unchanged when no bearing is preferred (existing callers).
    const angle =
      preferBearing !== undefined && tries < halfTries
        ? preferBearing + (rng() - 0.5) * DEFENSE.treeFrontArc
        : rng() * TWO_PI;
    const pos = ringPos(angle, rng() * DEFENSE.hazardRoughBand);
    if (valid(pos)) return made(pos);
  }
  // Fallback: sweep evenly-spaced bearings from the field-centre side for any clear,
  // in-bounds spot before settling (so a crowded green still avoids stacking).
  const base = Math.atan2(FIELD.height / 2 - target.center.y, FIELD.width / 2 - target.center.x);
  const sweeps = DEFENSE.hazardPlacementTries;
  for (let i = 0; i < sweeps; i++) {
    const pos = ringPos(base + (i / sweeps) * TWO_PI, 0);
    if (valid(pos)) return made(pos);
  }
  // Last resort (extremely crowded): the field-centre bearing, still in-bounds.
  return made(ringPos(base, 0));
}

/** Immutable updater: replace one side's SideState. */
function withSide(state: GameState, side: Side, next: SideState): GameState {
  return side === 'you' ? { ...state, you: next } : { ...state, rival: next };
}

/** The opponent of a side (the player a defending side attacks/harasses). */
function opponentOf(side: Side): Side {
  return side === 'you' ? 'rival' : 'you';
}

/** A fresh wind gust in a random direction at `windForce` for `windSeconds` (§3.7). */
function gust(rng: Rng): Wind {
  const angle = rng() * TWO_PI;
  return {
    remaining: DEFENSE.windSeconds,
    accel: vec(Math.cos(angle) * DEFENSE.windForce, Math.sin(angle) * DEFENSE.windForce),
  };
}

/**
 * Apply a played defensive card's effect to the state, returning the next state
 * (§3.7). The reducer debits energy and cycles the card; this applies ONLY the
 * effect:
 *
 *  - Bunker / Water / Moat (`card.hazard`): DEFENSE — drop a procedurally-sized
 *    hazard in the ROUGH around `side`'s OWN green, or sometimes around the
 *    OPPONENT's fairway (the one they hit off of) — never on a manicured surface,
 *    never around our own fairway. Stored on `side`'s own green (whose hazards
 *    `scoreShot` checks for the opponent's incoming shots), so it punishes the
 *    OPPONENT and never us; capped at `DEFENSE.maxHazardsPerGreen` (a full green is a
 *    no-op — the reducer still charges + cycles, matching the prototype guard).
 *  - Rough (`special: 'rough'`): rough a RANDOM tee box of `side`'s OPPONENT for
 *    `DEFENSE.roughSeconds`.
 *  - Wind (`special: 'wind'`): kick up a gust in a random direction for
 *    `DEFENSE.windSeconds` at `DEFENSE.windForce` (pushes every ball in flight).
 *
 * All randomness flows through the injected `rng` (HANDOFF §4.3). Returns a new
 * immutable GameState; never mutates the input.
 */
export function applyDefense(state: GameState, side: Side, card: DefenseCard, rng: Rng): GameState {
  // Wind: a shared, single global gust (§3.7) — affects both players' balls.
  if (card.special === 'wind') {
    return { ...state, wind: gust(rng) };
  }

  // The Gopher (legendary): arm a steal on the OPPONENT's NEXT shot (§3.7). No rng — the effect
  // is deterministic; it's consumed when that side next fires.
  if (card.special === 'gopher') {
    const foe = opponentOf(side);
    const foeState = foe === 'you' ? state.you : state.rival;
    return withSide(state, foe, { ...foeState, gopherPending: true });
  }

  // Rough: foul ONE of the opponent's FAIRWAY launch spots for a few seconds (§3.7, per-tee) —
  // saps power + sprays line for shots from THAT tee only. The LONG tee (its own teeing ground,
  // off the fairway) can't be roughed, so a random mid/short tee is picked via the rng.
  if (card.special === 'rough') {
    const foe = opponentOf(side);
    const foeState = foe === 'you' ? state.you : state.rival;
    const roughable = ['short', 'mid'] as const; // never the long tee box
    const key = roughable[Math.floor(rng() * roughable.length)];
    if (key === undefined) return state;
    const tee = foeState.tees[key];
    const tees = { ...foeState.tees, [key]: { ...tee, roughRemaining: DEFENSE.roughSeconds } };
    return withSide(state, foe, { ...foeState, tees });
  }

  // Hazard: DEFENSE. `scoreShot` checks the ATTACKED green's hazards, and the opponent
  // attacks OUR green, so the hazard is stored on `side`'s OWN green — it rings our green
  // and catches the opponent's incoming near-misses, never our own launches. Never on a
  // manicured surface; capped at `DEFENSE.maxHazardsPerGreen` (a full green is a no-op).
  if (card.hazard !== undefined && card.radius !== undefined) {
    const own = side === 'you' ? state.you : state.rival;
    const green = own.green;
    if (green.hazards.length >= DEFENSE.maxHazardsPerGreen) return state;
    // Procedural SIZE: scale the base radius by a per-hazard random factor.
    const size = DEFENSE.hazardSizeMin + rng() * (DEFENSE.hazardSizeMax - DEFENSE.hazardSizeMin);
    const radius = card.radius * size;
    const target: ShapeRegion = green; // ring our own green
    const avoid: readonly ShapeRegion[] = [
      state.you.green,
      state.rival.green,
      // Existing hazards as plain CONTAINER circles, so a new one never stacks on one.
      ...green.hazards.map((h) => ({ center: h.pos, radius: h.radius })),
    ];
    // A TREE is seated in FRONT of the green (toward field centre = the incoming shots) so it sits
    // on the attacker's line and can actually block; catch-hazards keep their full random ring.
    const preferBearing =
      card.hazard === 'tree'
        ? Math.atan2(FIELD.height / 2 - green.center.y, FIELD.width / 2 - green.center.x)
        : undefined;
    const hazard = placeHazard(target, card.hazard, radius, rng, avoid, preferBearing);
    // Final guarantee against STACKING: if a crowded green left only an overlapping
    // last-resort spot, drop it (no-op, like a full green — the reducer still charges).
    const stacks = green.hazards.some(
      (h) => dist(hazard.pos, h.pos) < hazard.radius + h.radius,
    );
    if (stacks) return state;
    const nextGreen: Green = { ...green, hazards: [...green.hazards, hazard] };
    return withSide(state, side, { ...own, green: nextGreen });
  }

  // No recognised effect (defensive guard): leave state unchanged.
  return state;
}
